import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { AtSign, Camera, Music2, Play, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Container } from './Primitives';
import { Button } from '../ui/Button';
import { useStoreBrandName } from '../../hooks/useStoreBrandName';
import { LanguageSwitcher } from '../common/LanguageSwitcher';

/** Placeholder brand URLs — replace with actual Moda MAI profiles in production */
const SOCIAL_LINKS = [
  { Icon: Camera, href: 'https://www.instagram.com/', label: 'Instagram' },
  { Icon: Users, href: 'https://www.facebook.com/', label: 'Facebook' },
  { Icon: Play, href: 'https://www.youtube.com/', label: 'YouTube' },
  { Icon: AtSign, href: 'https://twitter.com/', label: 'Twitter' },
  { Icon: Music2, href: 'https://www.tiktok.com/', label: 'TikTok' },
];

const Foot = styled.footer`
  background: ${({ theme }) => theme.colors.noir};
  color: ${({ theme }) => theme.colors.cream};
  margin-top: 2.5rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-top: 3rem;
  }
`;

const NewsletterBlock = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.noirSoft};
`;

const NewsletterInner = styled(Container)`
  padding: 3rem 1.5rem 3.25rem;
  max-width: 48rem;
  text-align: center;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 3.5rem 1.5rem 3.75rem;
  }
`;

const Kicker = styled.p`
  margin: 0 0 1rem;
  font-size: 11px;
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.25em;
  text-transform: uppercase;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.champagne};
`;

const Heading = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2.25rem, 4vw, 3rem);
  line-height: 1.05;
  text-wrap: balance;
`;

const Form = styled.form`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 32rem;
  margin-left: auto;
  margin-right: auto;
  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  background: transparent;
  border: 1px solid rgba(245, 242, 235, 0.3);
  padding: 0 1rem;
  height: 3rem;
  color: ${({ theme }) => theme.colors.cream};
  font-size: 0.875rem;
  outline: none;
  &::placeholder {
    color: rgba(245, 242, 235, 0.5);
  }
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.champagne};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.champagne};
  }
`;

const FinePrint = styled.p`
  margin: 1rem 0 0;
  font-size: 0.75rem;
  color: rgba(245, 242, 235, 0.5);
  max-width: 28rem;
  margin-left: auto;
  margin-right: auto;
`;

const ColGrid = styled(Container)`
  padding: 3.5rem 1.5rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2.5rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(4, 1fr);
    gap: 2.5rem;
  }
`;

const ColTitle = styled.p`
  margin: 0 0 1.25rem;
  font-size: 11px;
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.25em;
  text-transform: uppercase;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.champagne};
`;

const Ul = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  font-size: 0.875rem;
`;

const MutedLink = styled(Link)`
  color: rgba(245, 242, 235, 0.7);
  &:hover {
    color: ${({ theme }) => theme.colors.champagne};
  }
`;

const MutedA = styled.a`
  color: rgba(245, 242, 235, 0.7);
  &:hover {
    color: ${({ theme }) => theme.colors.champagne};
  }
`;

const RegionBlock = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.noirSoft};
`;

const RegionInner = styled(Container)`
  padding: 2.5rem 1.5rem;
`;

const BottomBar = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.noirSoft};
`;

const BottomInner = styled(Container)`
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const SocialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
`;

const SocialIcon = styled.a`
  color: rgba(245, 242, 235, 0.6);
  &:hover {
    color: ${({ theme }) => theme.colors.champagne};
  }
`;

export function Footer() {
  const brand = useStoreBrandName();
  const { t } = useTranslation();
  return (
    <Foot>
      <NewsletterBlock>
        <NewsletterInner>
          <Kicker>{t('modamai.footer.newsletterKicker', { brand })}</Kicker>
          <Heading>
            {t('modamai.footer.newsletterTitle')}
          </Heading>
          <Form onSubmit={(e) => e.preventDefault()}>
            <EmailInput type="email" required placeholder={t('modamai.footer.emailPlaceholder')} />
            <Button type="submit" $variant="gold" $size="lg">
              {t('modamai.footer.subscribe')}
            </Button>
          </Form>
          <FinePrint>
            {t('modamai.footer.finePrint', { brand })}
          </FinePrint>
        </NewsletterInner>
      </NewsletterBlock>

      <ColGrid>
        <div>
          <ColTitle>{t('modamai.footer.helpTitle')}</ColTitle>
          <Ul>
            {[
              ['Contact', '/contact'],
              ['Customer Service', '/customer-service'],
              ['Shipping', '/shipping'],
              ['Returns & Exchanges', '/returns'],
              ['Size Guide', '/size-guide'],
              ['FAQs', '/faq'],
            ].map(([l, h]) => (
              <li key={l}>
                <MutedLink to={h}>{l}</MutedLink>
              </li>
            ))}
          </Ul>
        </div>
        <div>
          <ColTitle>{t('modamai.footer.infoTitle')}</ColTitle>
          <Ul>
            {[
              ['Privacy Policy', '/privacy'],
              ['Terms & Conditions', '/terms'],
              ['Cookie Policy', '/cookies'],
              ['Blog', '/blog'],
              ['Reviews', '/reviews'],
              ['Account', '/account'],
            ].map(([l, h]) => (
              <li key={l}>
                <MutedLink to={h}>{l}</MutedLink>
              </li>
            ))}
          </Ul>
        </div>
        <div>
          <ColTitle>{t('modamai.footer.aboutTitle')}</ColTitle>
          <Ul>
            {[
              ['Our Story', '/about'],
              ['Sustainability', '/sustainability'],
              ['Accessibility', '/accessibility'],
              ['Careers', '/careers'],
              ['Contact', '/contact'],
            ].map(([l, h]) => (
              <li key={l}>
                <MutedLink to={h}>{l}</MutedLink>
              </li>
            ))}
          </Ul>
        </div>
        <div>
          <ColTitle>{t('modamai.footer.discountsTitle')}</ColTitle>
          <Ul>
            <li>
              <MutedA
                href="https://dealcouponz.com/stores/modamai"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('modamai.footer.couponLinkLabel')}
              </MutedA>
            </li>
          </Ul>
        </div>
      </ColGrid>

      <RegionBlock>
        <RegionInner>
          <p
            style={{
              textAlign: 'center',
              margin: '0 0 1.5rem',
              fontSize: '11px',
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
              color: '#F5F2EB',
            }}
          >
            {t('modamai.footer.chooseRegion')}
          </p>
          <LanguageSwitcher />
        </RegionInner>
      </RegionBlock>

      <BottomBar>
        <BottomInner>
          <SocialRow>
            {SOCIAL_LINKS.map(({ Icon, href, label }) => (
              <SocialIcon
                key={label}
                href={href}
                aria-label={label}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon size={20} strokeWidth={1.5} />
              </SocialIcon>
            ))}
          </SocialRow>
          <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(245,242,235,0.5)' }}>
            © {new Date().getFullYear()} {brand}. {t('modamai.footer.copyright')}
          </p>
        </BottomInner>
      </BottomBar>
    </Foot>
  );
}
